import { useState, useEffect, useCallback, useMemo } from "react";
import { useApi } from "./AuthContext";
import {
  Megaphone, Phone, Mail, MapPin, Calendar, Filter,
  ChevronDown, MessageSquare, Send, CheckCircle, XCircle,
  Trash2, FileText, ArrowRight, Home, TreePine, Leaf, User,
  Link2, Dog, Droplets, KeyRound
} from "lucide-react";
import { toast } from "sonner";
import { formatUSD } from "../utils/currency";
const STATUSES = [
  { key: "new", label: "New", color: "bg-blue-50 text-blue-700 border-blue-200" },
  { key: "contacted", label: "Contacted", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { key: "estimate_sent", label: "Estimate Sent", color: "bg-purple-50 text-purple-700 border-purple-200" },
  { key: "approved", label: "Approved", color: "bg-green-50 text-green-700 border-green-200" },
  { key: "lost", label: "Lost", color: "bg-stone-100 text-stone-500 border-stone-200" },
];

const SOURCE_LABELS = {
  contact_form: "Contact Form",
  estimate_calculator: "Calculator",
  manual_link: "Triage Link",
};

const STATUS_ACTIONS = {
  new: [
    { to: "approved", label: "Approve & Register", icon: CheckCircle, color: "text-green-600 hover:bg-green-50", isApprove: true, primary: true },
    { to: "contacted", label: "Mark Contacted", icon: MessageSquare, color: "text-amber-600 hover:bg-amber-50" },
    { to: "lost", label: "Mark Lost", icon: XCircle, color: "text-stone-400 hover:bg-stone-100" },
  ],
  contacted: [
    { to: "approved", label: "Approve & Register", icon: CheckCircle, color: "text-green-600 hover:bg-green-50", isApprove: true, primary: true },
    { to: "estimate_sent", label: "Estimate Sent", icon: Send, color: "text-purple-600 hover:bg-purple-50" },
    { to: "lost", label: "Mark Lost", icon: XCircle, color: "text-stone-400 hover:bg-stone-100" },
  ],
  estimate_sent: [
    { to: "approved", label: "Approve & Register", icon: CheckCircle, color: "text-green-600 hover:bg-green-50", isApprove: true, primary: true },
    { to: "lost", label: "Mark Lost", icon: XCircle, color: "text-stone-400 hover:bg-stone-100" },
  ],
  approved: [],
  lost: [
    { to: "new", label: "Reopen", icon: ArrowRight, color: "text-blue-600 hover:bg-blue-50" },
  ],
};

export default function LeadsModule({ readOnly = false }) {
  const api = useApi();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [expandedLead, setExpandedLead] = useState(null);
  // Approve flow
  const [approveModal, setApproveModal] = useState(null); // {lead}
  const [salesUsers, setSalesUsers] = useState([]);
  const [approveManager, setApproveManager] = useState("");
  const [approvePushToPipeline, setApprovePushToPipeline] = useState(true);
  const [approving, setApproving] = useState(false);

  const fetchLeads = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append("status", filterStatus);
      if (filterSource) params.append("source", filterSource);
      const { data } = await api.get(`/admin/leads?${params.toString()}`);
      setLeads(data);
    } catch (err) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [api, filterStatus, filterSource]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleStatusChange = async (lead, newStatus) => {
    try {
      await api.put(`/admin/leads/${lead.id}`, { status: newStatus });
      toast.success(`Status updated to "${STATUSES.find(s => s.key === newStatus)?.label}"`);
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    }
  };

  const openApproveModal = async (lead) => {
    setApproveModal({ lead });
    setApproveManager("");
    setApprovePushToPipeline(true);
    try {
      const { data } = await api.get("/admin/sales/users");
      setSalesUsers(data || []);
      if (data && data.length === 1) setApproveManager(data[0].id);
    } catch (_e) { setSalesUsers([]); }
  };

  const handleApprove = async () => {
    if (!approveModal) return;
    setApproving(true);
    try {
      const payload = approveManager
        ? { responsible_user_id: approveManager, create_deal: approvePushToPipeline }
        : {};
      const { data } = await api.post(`/admin/leads/${approveModal.lead.id}/approve`, payload);
      const dealMsg = data.deal ? " · added to Sales Pipeline backlog" : "";
      toast.success(`Lead approved! Client + SO created${dealMsg}.`);
      setApproveModal(null);
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve lead");
    } finally { setApproving(false); }
  };

  const handleDelete = async (lead) => {
    if (!window.confirm(`Delete lead "${lead.name}"?`)) return;
    try {
      await api.delete(`/admin/leads/${lead.id}`);
      toast.success("Lead deleted");
      fetchLeads();
    } catch (err) {
      toast.error("Failed to delete lead");
    }
  };

  const formatDate = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
  };

  const statusCounts = useMemo(() => STATUSES.map(s => ({
    ...s,
    count: leads.filter(l => l.status === s.key).length,
  })), [leads]);

  if (loading) return <div className="flex items-center justify-center h-64 text-stone-500">Loading...</div>;

  return (
    <div data-testid="leads-module" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-forest-50 rounded-lg flex items-center justify-center">
            <Megaphone className="w-5 h-5 text-forest-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-stone-800">Leads CRM</h2>
            <p className="text-sm text-stone-500">{leads.length} lead{leads.length !== 1 ? "s" : ""}</p>
          </div>
        </div>
      </div>

      {/* Pipeline Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {statusCounts.map(s => (
          <button
            key={s.key}
            data-testid={`pipeline-${s.key}`}
            onClick={() => setFilterStatus(filterStatus === s.key ? "" : s.key)}
            className={`p-3 rounded-xl border text-center transition-all ${
              filterStatus === s.key ? s.color + " ring-2 ring-offset-1 ring-current" : "bg-white border-stone-200 hover:border-stone-300"
            }`}
          >
            <div className="text-2xl font-bold">{s.count}</div>
            <div className="text-xs font-medium mt-0.5">{s.label}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center flex-wrap">
        <div className="flex items-center gap-2 text-sm text-stone-500">
          <Filter className="w-4 h-4" /> Filters:
        </div>
        <select
          data-testid="filter-source"
          value={filterSource}
          onChange={e => setFilterSource(e.target.value)}
          className="px-3 py-1.5 border border-stone-300 rounded-lg text-sm"
        >
          <option value="">All Sources</option>
          <option value="contact_form">Contact Form</option>
          <option value="estimate_calculator">Calculator</option>
          <option value="manual_link">Triage Link</option>
        </select>
        {(filterStatus || filterSource) && (
          <button onClick={() => { setFilterStatus(""); setFilterSource(""); }} className="text-xs text-stone-400 hover:text-stone-600 underline">
            Clear filters
          </button>
        )}
      </div>

      {/* Leads List */}
      <div className="space-y-3">
        {leads.length === 0 ? (
          <div className="bg-white rounded-xl border border-stone-200 p-12 text-center text-stone-400">
            No leads found
          </div>
        ) : leads.map(lead => {
          const status = STATUSES.find(s => s.key === lead.status) || STATUSES[0];
          const isExpanded = expandedLead === lead.id;
          const actions = STATUS_ACTIONS[lead.status] || [];
          const source = lead.lead_source || "contact_form";

          return (
            <div
              key={lead.id}
              data-testid={`lead-card-${lead.id}`}
              className="bg-white rounded-xl border border-stone-200 overflow-hidden hover:shadow-sm transition-shadow"
            >
              {/* Main Row */}
              <div
                className="px-5 py-4 flex items-center gap-4 cursor-pointer"
                onClick={() => setExpandedLead(isExpanded ? null : lead.id)}
              >
                {/* Source icon */}
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  source === "estimate_calculator" ? "bg-forest-50" :
                  source === "manual_link" ? "bg-purple-50" : "bg-blue-50"
                }`}>
                  {source === "estimate_calculator" ? (
                    <Home className="w-4 h-4 text-forest-600" />
                  ) : source === "manual_link" ? (
                    <Link2 className="w-4 h-4 text-purple-600" />
                  ) : (
                    <User className="w-4 h-4 text-blue-600" />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-stone-800 truncate">{lead.name}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${status.color}`}>
                      {status.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-stone-500 flex-wrap">
                    <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</span>
                    {lead.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{lead.email}</span>}
                    <span>{SOURCE_LABELS[source] || source}</span>
                  </div>
                  {/* Inline Approve & Register CTA — visible without expanding the card */}
                  {!readOnly && actions.some(a => a.isApprove) && (
                    <button
                      type="button"
                      data-testid={`lead-quick-approve-${lead.id}`}
                      onClick={(e) => { e.stopPropagation(); openApproveModal(lead); }}
                      className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-semibold shadow-sm transition-colors"
                      title="Convert this lead into a registered client immediately"
                    >
                      <CheckCircle className="w-3.5 h-3.5" /> Approve &amp; Register Client
                    </button>
                  )}
                </div>

                {/* Estimate value */}
                {lead.estimated_total > 0 && (
                  <div className="text-right flex-shrink-0">
                    <div className="text-lg font-bold text-forest-700">{formatUSD(lead.estimated_total)}</div>
                    <div className="text-xs text-stone-400">estimate</div>
                  </div>
                )}

                {/* Date + quick delete */}
                <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                  <div className="text-xs text-stone-400 hidden sm:block">
                    {formatDate(lead.created_at)}
                  </div>
                  <button
                    type="button"
                    data-testid={`lead-quick-delete-${lead.id}`}
                    onClick={(e) => { e.stopPropagation(); handleDelete(lead); }}
                    title="Delete lead"
                    aria-label="Delete lead"
                    className="p-1 rounded-md text-stone-300 hover:text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform flex-shrink-0 ${isExpanded ? "rotate-180" : ""}`} />
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="border-t border-stone-100 px-5 py-4 bg-stone-50/50">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* Left: Contact Info */}
                    <div className="space-y-2 text-sm">
                      {(lead.address || lead.city || lead.zip_code) && (
                        <div className="flex items-start gap-2 text-stone-600">
                          <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>
                            {lead.address}
                            {lead.city && (lead.address ? ", " : "") + lead.city}
                            {lead.zip_code && (lead.address || lead.city ? " " : "") + lead.zip_code}
                          </span>
                        </div>
                      )}
                      {lead.service_type && (
                        <div className="flex items-center gap-2 text-stone-600">
                          <FileText className="w-4 h-4 flex-shrink-0" />
                          <span>Service: {lead.service_type}</span>
                        </div>
                      )}
                      {lead.message && (
                        <div className="bg-white rounded-lg p-3 border border-stone-200 text-stone-600 text-sm italic">
                          "{lead.message}"
                        </div>
                      )}
                      {lead.created_at && (
                        <div className="flex items-center gap-2 text-stone-400 text-xs">
                          <Calendar className="w-3.5 h-3.5" />
                          <span>{formatDate(lead.created_at)}</span>
                        </div>
                      )}
                    </div>

                    {/* Right: Estimate Details */}
                    {source === "estimate_calculator" && (
                      <div className="space-y-2">
                        <div className="text-xs font-medium text-stone-500 uppercase tracking-wider">Estimate Details</div>
                        <div className="bg-white rounded-lg border border-stone-200 divide-y divide-stone-100">
                          {lead.property_size && (
                            <div className="px-3 py-2 flex items-center justify-between text-sm">
                              <span className="flex items-center gap-2 text-stone-600"><Leaf className="w-3.5 h-3.5 text-forest-500" /> Lawn Maintenance</span>
                              <span className="font-medium text-stone-800">{lead.property_size.replace(/_/g, " ")}</span>
                            </div>
                          )}
                          {lead.shrub_trimming && lead.shrub_trimming !== "none" && (
                            <div className="px-3 py-2 flex items-center justify-between text-sm">
                              <span className="flex items-center gap-2 text-stone-600"><TreePine className="w-3.5 h-3.5 text-forest-500" /> Shrub Trimming</span>
                              <span className="font-medium text-stone-800">{lead.shrub_trimming.replace(/_/g, " ")}</span>
                            </div>
                          )}
                          {lead.extra_shrubs > 0 && (
                            <div className="px-3 py-2 flex items-center justify-between text-sm">
                              <span className="text-stone-600">Extra Shrubs</span>
                              <span className="font-medium text-stone-800">{lead.extra_shrubs} shrubs</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Right: Triage Details */}
                    {source === "manual_link" && (
                      <div className="space-y-2">
                        <div className="text-xs font-medium text-stone-500 uppercase tracking-wider">Triage Details</div>
                        <div className="bg-white rounded-lg border border-stone-200 divide-y divide-stone-100">
                          {lead.usage_type && (
                            <div className="px-3 py-2 flex items-center justify-between text-sm">
                              <span className="text-stone-600">Usage</span>
                              <span className="font-medium text-stone-800 capitalize">{lead.usage_type}</span>
                            </div>
                          )}
                          {lead.referral_source && (
                            <div className="px-3 py-2 flex items-center justify-between text-sm">
                              <span className="text-stone-600">Referral</span>
                              <span className="font-medium text-stone-800 capitalize">{lead.referral_source.replace(/_/g, " ")}</span>
                            </div>
                          )}
                          <div className="px-3 py-2 flex items-center gap-4 text-sm">
                            <span className="flex items-center gap-1.5 text-stone-600">
                              <Dog className="w-3.5 h-3.5" /> Pets: <span className={`font-medium ${lead.has_pets ? "text-amber-700" : "text-stone-400"}`}>{lead.has_pets ? "Yes" : "No"}</span>
                            </span>
                            <span className="flex items-center gap-1.5 text-stone-600">
                              <Droplets className="w-3.5 h-3.5" /> Irrigation: <span className={`font-medium ${lead.has_irrigation ? "text-blue-700" : "text-stone-400"}`}>{lead.has_irrigation ? "Yes" : "No"}</span>
                            </span>
                          </div>
                          {lead.gate_code && (
                            <div className="px-3 py-2 flex items-center justify-between text-sm">
                              <span className="flex items-center gap-1.5 text-stone-600"><KeyRound className="w-3.5 h-3.5" /> Gate Code</span>
                              <span className="font-medium text-stone-800 font-mono">{lead.gate_code}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {!readOnly && actions.length > 0 && (
                    <div className="flex items-center gap-2 pt-3 border-t border-stone-200">
                      {actions.map(action => {
                        const Icon = action.icon;
                        return (
                          <button
                            key={action.to}
                            data-testid={`lead-action-${action.to}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (action.isApprove) openApproveModal(lead);
                              else handleStatusChange(lead, action.to);
                            }}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${action.color}`}
                          >
                            <Icon className="w-4 h-4" /> {action.label}
                          </button>
                        );
                      })}
                      <div className="flex-1" />
                      <button
                        data-testid="lead-delete"
                        onClick={(e) => { e.stopPropagation(); handleDelete(lead); }}
                        className="p-1.5 rounded-md hover:bg-red-50 text-stone-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Approve & Register Modal */}
      {approveModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="approve-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 bg-green-50 rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-stone-800">Approve & Register</h3>
                <p className="text-xs text-stone-500">{approveModal.lead.name} · {approveModal.lead.phone}</p>
              </div>
            </div>
            <p className="text-sm text-stone-600 mt-3 mb-4">
              The lead will be converted into a <strong>Client</strong> with all data and triage notes inherited.
            </p>
            <label className="block text-sm font-medium text-stone-700 mb-1">Assign Responsible Manager</label>
            <select
              data-testid="approve-manager-select"
              value={approveManager}
              onChange={(e) => setApproveManager(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500 mb-3"
            >
              <option value="">— Don't assign yet —</option>
              {salesUsers.map(u => (
                <option key={u.id} value={u.id}>{u.name} {u.role ? `(${u.role})` : ""}</option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-sm text-stone-700 mb-5">
              <input
                data-testid="approve-pipeline-checkbox"
                type="checkbox"
                checked={approvePushToPipeline}
                disabled={!approveManager}
                onChange={(e) => setApprovePushToPipeline(e.target.checked)}
                className="rounded"
              />
              Push to <strong>Sales Pipeline backlog</strong> for the manager
            </label>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setApproveModal(null)}
                disabled={approving}
                className="px-4 py-2 text-sm rounded-lg border border-stone-300 text-stone-600 hover:bg-stone-50"
              >Cancel</button>
              <button
                data-testid="approve-confirm-btn"
                onClick={handleApprove}
                disabled={approving}
                className="px-5 py-2 text-sm rounded-lg bg-forest-600 hover:bg-forest-700 text-white font-semibold shadow disabled:opacity-60"
              >{approving ? "Approving…" : "Approve & Register"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
